import type React from "react";
import { memo, useMemo } from "react";
import { ErrorSeverity } from "../../error-handling/error-handler";
import { errorUI } from "../../error-handling/index";
import type { CommenterDefinition } from "../../types/core";
import { getMergedCommenterMap } from "../../utils/commenter-utils";
import { formatTime } from "../../utils/date-formatter";
import { useSmartScroll } from "../hooks/useSmartScroll";
import { UI_ICONS } from "../icons";

export interface TranscriptSegment {
	text: string;
	isFinal: boolean;
	timestamp: Date;
	id: string;
	type: "speech" | "comment";
	confidence: number;
	commenterId?: string;
}

interface TranscriptionDisplayProps {
	segments: TranscriptSegment[];
	commenterDefinitions: CommenterDefinition[];
	showTimestamps: boolean;
	lowConfidenceThreshold: number;
	onSegmentUpdate?: (id: string, newText: string) => void;
	onSegmentDelete?: (id: string) => void;
}

interface TranscriptItemProps {
	segment: TranscriptSegment;
	commenterDef?: CommenterDefinition | null;
	showTimestamps: boolean;
	lowConfidenceThreshold: number;
	onSegmentUpdate?: (id: string, newText: string) => void;
	onSegmentDelete?: (id: string) => void;
}

/**
 * Individual transcript item component, memoized to prevent unnecessary re-renders.
 * Especially important during live transcription where the segments array updates frequently.
 */
const TranscriptItem: React.FC<TranscriptItemProps> = memo(
	({
		segment,
		commenterDef,
		showTimestamps,
		lowConfidenceThreshold,
		onSegmentUpdate,
		onSegmentDelete,
	}) => {
		const isComment = segment.type === "comment";
		const safeText = String(segment.text || "");

		const handleCopy = async () => {
			try {
				await navigator.clipboard.writeText(safeText);
				errorUI.showNotification(
					"文字起こしをコピーしました。",
					ErrorSeverity.LOW,
				);
			} catch (err) {
				console.error("Failed to copy transcript segment:", err);
				errorUI.showNotification(
					"コピーに失敗しました。",
					ErrorSeverity.MEDIUM,
				);
			}
		};

		const getClassName = () => {
			let className =
				"flex items-start gap-2 py-1 hover:bg-white/5 rounded group animate-in fade-in slide-in-from-left-5 duration-300";
			if (isComment) {
				className +=
					" bg-blue-900/20 border-l-4 border-primary px-3 py-2 my-1 text-blue-200 rounded-r";
			} else {
				className += segment.isFinal
					? " text-text-main"
					: " text-text-dim opacity-70";
				if (segment.isFinal && segment.confidence < lowConfidenceThreshold) {
					className +=
						" bg-orange-500/10 border-l-4 border-orange-400 px-2 my-0.5 rounded-r transition-colors hover:bg-orange-500/20";
				}
			}
			return className;
		};

		return (
			<div className={getClassName()} data-segment-id={segment.id}>
				{isComment && (
					<span className="flex items-center gap-1 bg-primary/20 px-2 py-0.5 rounded-full text-xs font-bold text-primary shrink-0">
						{commenterDef ? (
							<>
								<span className="text-sm" role="img" aria-hidden="true">
									{commenterDef.icon}
								</span>
								<span className="whitespace-nowrap">{commenterDef.name}</span>
							</>
						) : (
							"AI"
						)}
					</span>
				)}
				{segment.isFinal && segment.type === "speech" ? (
					// biome-ignore lint/a11y/useSemanticElements: div with contentEditable is used for inline editing with custom styling
					<div
						className={`flex-1 break-words hyphens-auto font-medium cursor-text outline-none rounded px-1 transition-all hover:bg-primary/10 hover:shadow-[0_0_0_1px_rgba(99,102,241,0.2)] focus:bg-card-bg focus:shadow-[0_0_0_2px_rgba(99,102,241,0.4)] min-w-[20px]
						${segment.confidence < lowConfidenceThreshold ? "text-orange-200 underline decoration-wavy decoration-orange-400" : ""}`}
						role="textbox"
						aria-label={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」を編集`}
						title={
							segment.confidence < lowConfidenceThreshold
								? "信頼度が低いため、誤変換の可能性があります"
								: undefined
						}
						tabIndex={0}
						contentEditable
						suppressContentEditableWarning
						onBlur={(e) => {
							const newText = e.target.textContent?.trim() || "";
							if (newText !== safeText && onSegmentUpdate) {
								onSegmentUpdate(segment.id, newText);
							}
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault();
								(e.target as HTMLElement).blur();
							}
							if (e.key === "Escape") {
								e.preventDefault();
								(e.target as HTMLElement).textContent = safeText;
								(e.target as HTMLElement).blur();
							}
						}}
					>
						{safeText}
					</div>
				) : (
					// biome-ignore lint/a11y/useSemanticElements: role="status" is used for live updates of transcription
					<div
						className={`flex-1 break-words hyphens-auto ${!segment.isFinal ? "italic relative after:content-['|'] after:animate-pulse after:ml-0.5" : "font-medium"}`}
						role="status"
					>
						{safeText}
					</div>
				)}
				{segment.isFinal && (
					<div className="flex gap-1 order-last shrink-0">
						<button
							type="button"
							className="opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded cursor-pointer text-text-dim hover:text-primary transition-all p-1 leading-none active:scale-95"
							title={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」をコピー`}
							aria-label={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」をコピー`}
							onClick={handleCopy}
						>
							{UI_ICONS.COPY}
						</button>
						<button
							type="button"
							className="opacity-0 group-hover:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded cursor-pointer text-text-dim hover:text-red-500 transition-all p-1 leading-none active:scale-95"
							title={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」を削除`}
							aria-label={`発言「${safeText.slice(0, 20)}${safeText.length > 20 ? "..." : ""}」を削除`}
							onClick={() => onSegmentDelete?.(segment.id)}
						>
							{UI_ICONS.DELETE}
						</button>
					</div>
				)}
				{showTimestamps && (
					<span className="text-xs text-text-dim font-mono whitespace-nowrap mt-1 order-last opacity-60">
						{formatTime(segment.timestamp)}
					</span>
				)}
			</div>
		);
	},
);

/**
 * TranscriptionDisplay component
 * Optimized with React.memo and TranscriptItem memoization to prevent unnecessary re-renders during live transcription.
 */
export const TranscriptionDisplay: React.FC<TranscriptionDisplayProps> = memo(
	({
		segments,
		commenterDefinitions,
		showTimestamps,
		lowConfidenceThreshold,
		onSegmentUpdate,
		onSegmentDelete,
	}) => {
		const containerRef = useSmartScroll<HTMLDivElement>(segments.length);

		// Performance optimization: Use pre-calculated static map to avoid O(N*M) iterations in useMemo
		const commenterMap = useMemo(
			() => getMergedCommenterMap(commenterDefinitions),
			[commenterDefinitions],
		);

		return (
			<div
				className="flex-1 overflow-y-auto scrollbar-none p-4 bg-white/5"
				ref={containerRef}
			>
				<div
					className="min-h-full flex flex-col gap-2"
					role="log"
					aria-live="polite"
					aria-label="ライブ文字起こし"
				>
					{segments.length === 0 ? (
						<div className="text-text-dim text-center mt-[50%] -translate-y-1/2 flex flex-col items-center gap-2 animate-in fade-in duration-500">
							<span
								className="text-4xl mb-2 opacity-50"
								role="img"
								aria-hidden="true"
							>
								{UI_ICONS.TRANSCRIPT}
							</span>
							<p className="font-semibold mb-1">
								文字起こしがここに表示されます
							</p>
							<p className="text-xs opacity-70">
								話した内容がリアルタイムで反映されます
							</p>
						</div>
					) : (
						segments.map((segment) => {
							const isComment = segment.type === "comment";
							// Efficient O(1) lookup for commenter definition
							const commenterDef =
								isComment && segment.commenterId
									? commenterMap.get(segment.commenterId)
									: null;

							return (
								<TranscriptItem
									key={segment.id}
									segment={segment}
									commenterDef={commenterDef}
									showTimestamps={showTimestamps}
									lowConfidenceThreshold={lowConfidenceThreshold}
									onSegmentUpdate={onSegmentUpdate}
									onSegmentDelete={onSegmentDelete}
								/>
							);
						})
					)}
				</div>
			</div>
		);
	},
);
